define((require) => {
    "use strict";

    const Action                = require("shared/ui/experience/action/Action");
    const ActionCollection      = require("shared/ui/experience/action/ActionCollection");
    const ActionFactory         = require("shared/ui/experience/action/ActionFactory");
    const AnimationCollection   = require("shared/ui/experience/widgets/animations/AnimationCollection");
    const Backbone              = require("backbone");
    const EffectModelCollection = require("shared/ui/experience/action/effects/EffectModelCollection");
    const ImprovedModel         = require("shared/zbone/ImprovedModel");
    const Scale                 = require("shared/util/Scale");
    const WidgetScaleMode       = require("shared/ui/experience/widgets/WidgetScaleMode");

    /**
     * Base model for widgets.
     *
     * @class AbstractWidgetModel
     * @constructor
     */
    const AbstractWidgetModel = ImprovedModel.extend({

        // Implicitly also "scene: Scene" relation on every widget model because of the (reverse) relation mapping defined in Scene.js

        relations: [{
            type: Backbone.Many,
            key: "animations",
            collectionType: AnimationCollection
        }, {
            type: Backbone.Many,
            key: "effects",
            collectionType: EffectModelCollection
        }, {
            type: Backbone.One,
            key: "action",
            relatedModel: Action,
            // See http://dhruvaray.github.io/backbone-associations/specify-associations.html#sa-map
            map(value) {
                // TODO: Precondition?
                // Only map for raw object representations
                return value instanceof Action ? value : ActionFactory.newActionFromType(value.type, value);
            }
        }, {
            type: Backbone.Many,
            key: "actions",
            collectionType: ActionCollection
        }],

        defaults: {

            left: 0,

            top: 0,

            rotation: 0,

            layerIndex: 0,

            /**
             * Widget opacity - the the computed 'opacity' property below.
             *
             * @type {Number}
             */
            opacity: 1,

            /**
             * Optional action to be activated by the view on interaction.
             *
             * @type {Action}
             */
            action: null,

            /**
             * @deprecated Use action instead (we only allow a single action from now on).
             *
             * @type {ActionCollection}
             */
            actions: [],

            /**
             * Array of effects to be played by the view on interaction.
             *
             * @type {EffectModelCollection}
             */
            effects: [],

            animations: [],

            /**
             * Represents the current scale of the widget. Defaults to 1.0
             *
             * @type {Scale}
             */
            scale: new Scale(),

            /**
             * Defines the sizing strategy when scaled.
             *
             * @type {String}
             */
            scaleMode: WidgetScaleMode.DEFAULT,

            /**
             * Defines the title of the element.
             *
             * @type {String}
             */
            title: "",

            isVisible: true,

            isDraggable: true,

            isLocked: false,

            isEditorHidden: false
        },

        // TODO: these are editor functionality!
        embedded:     true,
        transient:    ["scale", "scaleMode", "isTransforming"],

        computed: {
            scaledWidth: {
                deps: ["scale", "width"],
                value(scale, width) {
                    return scale.x * width;
                }
            },

            scaledHeight: {
                deps: ["scale", "height"],
                value(scale, height) {
                    return scale.y * height;
                }
            },

            scaledTop: {
                deps: ["scale", "top"],
                value(scale, top) {
                    return scale.y * top;
                }
            },

            scaledLeft: {
                deps: ["scale", "left"],
                value(scale, left) {
                    return scale.x * left;
                }
            },

            // HACK: Regardless of persisted 'opacity', we force the 'opacity' value to 1.0 (due to legacy widget opacity values - see MOS-2375)
            // Remove this once we're comfortable.
            //
            // See ShapeWidgetModel for how to implement 'opacity' support...
            opacity: {
                deps: ["opacity"],
                value() {
                    return 1.0;
                }
            }
        },

        hasActions() {
            return this.has("action") || this.get("actions").length !== 0;
        },

        /**
         * Returns true if this model has any activateable actions, false otherwise. <p>
         *
         * An activateable Action is an action whose <tt>action.activateable(..)</tt> method returns true.
         *
         * @param  {ExperienceViewModel} experienceViewModel The experience view model; can be falsey in which case
         *                                                   false is returned.
         * @return {Boolean} True if this model has any activateable actions, false otherwise.
         * @see    Action#activateable
         */
        hasActivateableActions(experienceViewModel) {
            const action = this.get("action");
            if (action) {
                return action.activateable(experienceViewModel);
            }
            // Legacy collection of actions...
            const actions = this.get("actions");
            let i = actions.length;

            while (i--) {
                if (actions.at(i).activateable(experienceViewModel)) {
                    return true;
                }
            }
            return !!experienceViewModel;
        },

        hasEffects() {
            return this.get("effects").length !== 0;
        },

        /**
         * Whether the widget is considered interactive, i.e. contains (activateable) actions or effects. <p>
         *
         * If an <tt>ExperienceViewModel</tt> is supplied as argument, actions, if any, will be tested for whether
         * or not they are currently activateable or not.
         *
         * @param  {ExperienceViewModel} [experienceViewModel] The experience view model; can be falsey, in which
         *                                                     case this function only test for the presence of
         *                                                     actions on this model, not if such actions are
         *                                                     considered activateable or not.
         *
         * @return {Boolean} <tt>true</tt> if the widget contains any actions or effects, <tt>false</tt> if not.
         */
        isInteractive(experienceViewModel) {
            return this.hasEffects() || (experienceViewModel ? this.hasActivateableActions(experienceViewModel) : this.hasActions());
        },

        /**
         * Given a set of widgets from a scene, return all widgets that are siblings
         * of this one within a widget group. Include the widget itself.
         * @param sceneWidgets
         */
        getGroupSiblings(sceneWidgets) {
            let siblings = null;
            sceneWidgets.forEach((widget) => {
                if (widget.has("group")) {
                    if (widget.get("group").models.map((widget) => widget.get("uuid")).includes(this.get("uuid"))) {
                        siblings = widget.get("group");
                    }
                }
            });
            return siblings;
        },

        getScaledSize(scaleX, scaleY) {
            const size = {};

            if (this.has("width")) {
                size.width = this.get("width") * scaleX;
            }

            if (this.has("height")) {
                size.height = this.get("height") * (scaleY || scaleX);
            }

            return size;
        },

        /**
         * Whether the widget model is considered to be in a "valid" state.
         *
         * E.g. a text based widget might be "invalid" if the text property is blank.
         *
         * Be default, all widget models are considered valid.
         *
         * @return {Boolean} <tt>true</tt> if the widget is considered valid, <tt>false</tt> if not.
         */
        isValid() {
            return true;
        }

    });

    return AbstractWidgetModel;
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/AbstractWidgetModel.js